import { ROLES, type UserRole } from '@shared/config/constants'
import { ROUTES } from '@shared/config/routes'

export interface NavItem {
  key: string
  i18nKey: string
  path: string
  icon: string // path to inactive icon SVG
  iconActive?: string // path to active icon SVG (swapped when NavLink isActive)
  /** Extra route prefixes that should also mark this item active (beyond `path`). */
  activePaths?: string[]
}

const adminNav: NavItem[] = [
  {
    key: 'home',
    i18nKey: 'menu.home',
    path: ROUTES.ADMIN,
    icon: '/images/index_noactive.svg',
    iconActive: '/images/index_active.svg',
  },
  {
    key: 'users',
    i18nKey: 'menu.users',
    path: ROUTES.ADMIN_USERS,
    icon: '/images/users_noactive.svg',
    iconActive: '/images/users_active.svg',
  },
  {
    key: 'olympiads',
    i18nKey: 'menu.olympiads',
    path: ROUTES.ADMIN_OLYMPIADS,
    icon: '/images/olympiad_noactive.svg',
    iconActive: '/images/olympiad_active.svg',
  },
  {
    key: 'tasks',
    i18nKey: 'menu.tasks',
    path: ROUTES.ADMIN_TASKS,
    icon: '/images/task_noactive.svg',
    iconActive: '/images/task_active.svg',
  },
  {
    key: 'results',
    i18nKey: 'menu.results',
    path: ROUTES.ADMIN_RESULTS,
    icon: '/images/ranking_noactive.svg',
    iconActive: '/images/ranking_active.svg',
  },
  {
    key: 'certificates',
    i18nKey: 'menu.certificates',
    path: ROUTES.ADMIN_CERTIFICATES,
    icon: '/images/certificate_noactive.svg',
    iconActive: '/images/certificate_active.svg',
  },
  {
    key: 'payments',
    i18nKey: 'menu.payments',
    path: ROUTES.ADMIN_PAYMENTS,
    icon: '/images/card_noactive.svg',
    iconActive: '/images/card_active.svg',
  },
  {
    // Один пункт на весь раздел: объявления, переписка с представителями и
    // тех. поддержка. Прежде их было два — с одинаковой иконкой, потому что
    // своей у поддержки нет, — и различить их в меню было нечем (замечание
    // владельца 2026-08-22).
    key: 'messages',
    i18nKey: 'menu.messages',
    path: ROUTES.ADMIN_CHAT,
    icon: '/images/chat_noactive.svg',
    iconActive: '/images/chat_active.svg',
  },
]

const participantNav: NavItem[] = [
  {
    key: 'home',
    i18nKey: 'menu.home',
    path: ROUTES.PARTICIPANT_DASHBOARD,
    icon: '/images/index_noactive.svg',
    iconActive: '/images/index_active.svg',
  },
  {
    key: 'olympiads',
    i18nKey: 'menu.olympiads',
    path: ROUTES.PARTICIPANT_OLYMPIADS,
    icon: '/images/olympiad_noactive.svg',
    iconActive: '/images/olympiad_active.svg',
  },
  {
    key: 'tasks',
    i18nKey: 'menu.tasks',
    path: ROUTES.PARTICIPANT_TASKS,
    icon: '/images/task_noactive.svg',
    iconActive: '/images/task_active.svg',
  },
  // Порядок «Сертификаты → Рейтинг» взят из макета участника (499:139111)
  {
    key: 'certificates',
    i18nKey: 'menu.certificates',
    path: ROUTES.PARTICIPANT_CERTIFICATES,
    icon: '/images/certificate_noactive.svg',
    iconActive: '/images/certificate_active.svg',
  },
  {
    key: 'ranking',
    i18nKey: 'participant:menu.ranking',
    path: ROUTES.PARTICIPANT_RATE_OVERALL,
    icon: '/images/ranking_noactive.svg',
    iconActive: '/images/ranking_active.svg',
    activePaths: [ROUTES.PARTICIPANT_RATE_OLYMPIAD],
  },
  {
    key: 'payments',
    i18nKey: 'participant:menu.balance_and_payments',
    path: ROUTES.PARTICIPANT_PAYMENTS,
    icon: '/images/card_noactive.svg',
    iconActive: '/images/card_active.svg',
  },
  {
    key: 'messages',
    i18nKey: 'menu.messages',
    path: ROUTES.PARTICIPANT_MESSAGES,
    icon: '/images/chat_noactive.svg',
    iconActive: '/images/chat_active.svg',
  },
  {
    key: 'instructions',
    i18nKey: 'participant:menu.instructions',
    path: ROUTES.PARTICIPANT_INSTRUCTIONS,
    icon: '/images/instructions_noactive.svg',
    iconActive: '/images/instructions_active.svg',
  },
  {
    key: 'faq',
    i18nKey: 'participant:menu.faq',
    path: ROUTES.PARTICIPANT_FAQ,
    icon: '/images/faq_noactive.svg',
    iconActive: '/images/faq_active.svg',
  },
]

const representativeNav: NavItem[] = [
  {
    key: 'home',
    i18nKey: 'menu.home',
    path: ROUTES.REPRESENTATIVE,
    icon: '/images/index_noactive.svg',
    iconActive: '/images/index_active.svg',
  },
  {
    key: 'members',
    i18nKey: 'participants',
    path: ROUTES.REPRESENTATIVE_MEMBERS,
    icon: '/images/users_noactive.svg',
    iconActive: '/images/users_active.svg',
  },
  {
    key: 'results',
    i18nKey: 'menu.results',
    path: ROUTES.REPRESENTATIVE_RESULTS,
    icon: '/images/ranking_noactive.svg',
    iconActive: '/images/ranking_active.svg',
  },
  {
    // Раздел тот же, что у администратора и участника, — и называется так же.
    key: 'messages',
    i18nKey: 'menu.messages',
    path: ROUTES.REPRESENTATIVE_CHAT,
    icon: '/images/chat_noactive.svg',
    iconActive: '/images/chat_active.svg',
  },
]

export const profileNavItem: NavItem = {
  key: 'profile',
  i18nKey: 'common:profile',
  path: '', // resolved per-role in Sidebar
  icon: '/images/users_noactive.svg',
  iconActive: '/images/users_active.svg',
}

export const NAV_CONFIG: Record<UserRole, NavItem[]> = {
  [ROLES.ADMIN]: adminNav,
  [ROLES.PARTICIPANT]: participantNav,
  [ROLES.REPRESENTATIVE]: representativeNav,
}
